import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import {
    alpha,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Stack,
    Typography,
} from '@mui/material';
import React from 'react';
import { capitalizeName } from '../../../../shared/utils';
import { Experience } from '../../../types/experience';

type OwnerListingMobileCardProps = {
    experience: Experience;
    bookingLabel: string;
    capacityLabel: string;
    hireRangeLabel: string;
    locationLabel: string;
    priceLabel: string;
    subTypeLabel: string;
    isUnpublishing: boolean;
    onOpen: (experience: Experience) => void;
    onEdit: (experience: Experience) => void;
    onUnpublish: (experience: Experience) => void;
    onDelete: (experience: Experience) => void;
};

const OwnerListingMobileCard = ({
    experience,
    bookingLabel,
    capacityLabel,
    hireRangeLabel,
    locationLabel,
    priceLabel,
    subTypeLabel,
    isUnpublishing,
    onOpen,
    onEdit,
    onUnpublish,
    onDelete,
}: OwnerListingMobileCardProps) => {
    const isUnpublished = experience?.isAvailable === false;
    const photoSrc = experience?.photos?.[0]?.src;
    const nameColor = isUnpublished ? 'text.secondary' : 'text.primary';
    const detailColor = 'text.secondary';

    return (
        <Card
            variant='outlined'
            sx={{
                borderRadius: 2,
                overflow: 'hidden',
                borderColor: 'divider',
                opacity: isUnpublished ? 0.85 : 1,
            }}
        >
            <CardActionArea
                component='div'
                onClick={() => onOpen(experience)}
                sx={{ alignItems: 'stretch' }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        aspectRatio: '4 / 3',
                    }}
                >
                    <Box
                        component='img'
                        src={photoSrc}
                        alt=''
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                            bgcolor: 'action.hover',
                            opacity: isUnpublished ? 0.55 : 1,
                        }}
                    />
                    {isUnpublished && (
                        <Box
                            sx={(theme) => ({
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: alpha(
                                    theme.palette.black.main,
                                    0.6,
                                ),
                            })}
                        >
                            <VisibilityOffIcon
                                sx={{
                                    fontSize: 48,
                                    color: 'neutral.main',
                                    opacity: 0.7,
                                }}
                            />
                        </Box>
                    )}
                </Box>

                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack spacing={1.25}>
                        <Stack
                            direction='row'
                            justifyContent='space-between'
                            alignItems='flex-start'
                            spacing={1}
                        >
                            <Typography
                                variant='h5'
                                color={nameColor}
                                sx={{ fontWeight: 700, minWidth: 0 }}
                            >
                                {experience.name
                                    ? capitalizeName(experience.name)
                                    : 'New Draft'}
                                {experience.status === 'draft' && (
                                    <Chip
                                        label='Draft'
                                        size='small'
                                        sx={{ ml: 1 }}
                                    />
                                )}
                            </Typography>
                            <Typography
                                variant='body3'
                                color={detailColor}
                                sx={{
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                }}
                            >
                                {locationLabel || '—'}
                            </Typography>
                        </Stack>

                        <Typography variant='body2' color={detailColor}>
                            {experience.streetAddress
                                ? capitalizeName(experience.streetAddress)
                                : '—'}
                        </Typography>

                        <Stack
                            direction='row'
                            justifyContent='space-between'
                            alignItems='flex-start'
                            spacing={1}
                        >
                            <Stack spacing={0.5}>
                                <Typography
                                    variant='body3'
                                    color={nameColor}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <PeopleOutlineIcon
                                        sx={{
                                            fontSize: 16,
                                            mr: 0.5,
                                            color: detailColor,
                                        }}
                                    />
                                    {capacityLabel}
                                </Typography>
                                <Typography
                                    variant='body3'
                                    color={nameColor}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <AccessTimeIcon
                                        sx={{
                                            fontSize: 16,
                                            mr: 0.5,
                                            color: detailColor,
                                        }}
                                    />
                                    {hireRangeLabel}
                                </Typography>
                            </Stack>

                            <Stack spacing={0.5} alignItems='flex-end'>
                                <Typography
                                    variant='body3'
                                    color={nameColor}
                                    sx={{ fontWeight: 700 }}
                                >
                                    {subTypeLabel}
                                </Typography>
                                <Typography
                                    variant='body3'
                                    color={nameColor}
                                    sx={{ fontWeight: 700 }}
                                >
                                    {priceLabel}
                                </Typography>
                            </Stack>
                        </Stack>

                        <Typography variant='body3' color={detailColor}>
                            Booked: {bookingLabel}
                        </Typography>

                        <Stack
                            direction='row'
                            spacing={1}
                            onClick={(event) => event.stopPropagation()}
                            onMouseDown={(event) => event.stopPropagation()}
                        >
                            <Button
                                type='button'
                                variant='contained'
                                size='small'
                                startIcon={<EditOutlinedIcon />}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onEdit(experience);
                                }}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: 1.5,
                                    px: 1.5,
                                    minWidth: 0,
                                    flex: 1,
                                }}
                            >
                                Edit
                            </Button>
                            {experience.status === 'draft' ? (
                                <Button
                                    type='button'
                                    variant='outlined'
                                    color='error'
                                    size='small'
                                    startIcon={<DeleteOutlinedIcon />}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onDelete(experience);
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderRadius: 1.5,
                                        px: 1.5,
                                        minWidth: 0,
                                        flex: 1,
                                    }}
                                >
                                    Delete
                                </Button>
                            ) : (
                                <Button
                                    type='button'
                                    variant='outlined'
                                    size='small'
                                    startIcon={<VisibilityOffOutlinedIcon />}
                                    disabled={isUnpublished || isUnpublishing}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onUnpublish(experience);
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderRadius: 1.5,
                                        px: 1.5,
                                        minWidth: 0,
                                        flex: 1,
                                    }}
                                >
                                    Unpublish
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

export default OwnerListingMobileCard;
